import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { handleSessionExpired } from '../utils/api';
import { clearExternalAccess, getCrmToken, getMsUntilExpired, hasExternalAccess } from '../utils/externalAccess';

// setTimeout overflows and fires immediately above this delay.
const MAX_TIMER_MS = 2 ** 31 - 1;

export default function EndpointAccessGuard({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const allowed = hasExternalAccess();

  useEffect(() => {
    if (allowed) return undefined;

    // A token that is stored but no longer allowed has expired; no token means never signed in.
    const expired = Boolean(getCrmToken());
    clearExternalAccess();
    const next = `${location.pathname}${location.search}`;
    navigate(`/login?next=${encodeURIComponent(next)}${expired ? '&reason=expired' : ''}`, { replace: true });
    return undefined;
  }, [allowed, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!allowed) return undefined;

    let timer;
    // Re-checked on tab focus because browsers throttle timers in background tabs, so the
    // timer alone can fire long after the token died.
    const arm = () => {
      clearTimeout(timer);
      if (!hasExternalAccess()) {
        handleSessionExpired();
        return;
      }
      const remaining = getMsUntilExpired();
      if (remaining !== null) timer = setTimeout(arm, Math.min(remaining + 1000, MAX_TIMER_MS));
    };

    arm();
    document.addEventListener('visibilitychange', arm);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', arm);
    };
  }, [allowed]);

  return allowed
    ? children
    : <main className="splash" aria-live="polite">Validating access…</main>;
}
